import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type {
  PhotoResponse,
  PhotoParentType,
  RequestPhotoUploadUrlsRequest,
  PhotoUploadUrlsResponse,
  PhotoFile
} from "@/types/photos";

// Hook for fetching photos by parent
export function usePhotos(parentType: PhotoParentType | null, parentId: string | null) {
  const {
    data: photos = [],
    isLoading,
    error
  } = useQuery<PhotoResponse[]>({
    queryKey: ["/photos", parentType, parentId],
    queryFn: async () => {
      if (!parentType || !parentId) throw new Error("Parent type and ID are required");
      const response = await apiRequest("GET", `/photos/${parentType}/${parentId}`);
      return response.data || response;
    },
    enabled: !!parentType && !!parentId
  });

  return {
    photos,
    isLoading,
    error
  };
}

// Hook for requesting photo upload URLs
export function useRequestPhotoUploadUrls() {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (request: RequestPhotoUploadUrlsRequest) => {
      const response = await apiRequest("POST", "/photos", request);
      return (response.data || response) as PhotoUploadUrlsResponse;
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to request upload URLs. Please try again.",
        variant: "destructive"
      });
    }
  });
}

// Hook for confirming photo upload
export function useConfirmPhotoUpload() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (photoId: string) => {
      const response = await apiRequest("POST", `/photos/${photoId}/confirm`);
      return (response.data || response) as PhotoResponse;
    },
    onSuccess: (data) => {
      // Invalidate the photos query for this parent
      queryClient.invalidateQueries({
        queryKey: ["/photos", data.parentType, data.parentId]
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to confirm photo upload. Please try again.",
        variant: "destructive"
      });
    }
  });
}

// Hook for deleting a photo
export function useDeletePhoto() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (photoId: string) => {
      await apiRequest("DELETE", `/photos/${photoId}`);
      return photoId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/photos"] });
      toast({
        title: "Photo deleted",
        description: "Photo has been deleted successfully."
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete photo. Please try again.",
        variant: "destructive"
      });
    }
  });
}

// Hook for setting primary photo
export function useSetPrimaryPhoto() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ parentType, parentId, photoId }: {
      parentType: PhotoParentType;
      parentId: string;
      photoId: string
    }) => {
      const response = await apiRequest("PUT", `/photos/${parentType}/${parentId}/primary/${photoId}`);
      return response.data || response;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["/photos", variables.parentType, variables.parentId]
      });
      toast({
        title: "Primary photo updated",
        description: "Primary photo has been set successfully."
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to set primary photo. Please try again.",
        variant: "destructive"
      });
    }
  });
}

// Utility function to upload file to Azure blob storage
export async function uploadFileToAzure(file: File, uploadUrl: string): Promise<void> {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'x-ms-blob-type': 'BlockBlob',
      'Content-Type': file.type,
    },
    body: file,
  });

  if (!response.ok) {
    throw new Error(`Failed to upload file: ${response.statusText}`);
  }
}

// Complete upload workflow hook
export function usePhotoUploadWorkflow() {
  const requestUrls = useRequestPhotoUploadUrls();
  const confirmUpload = useConfirmPhotoUpload();
  const { toast } = useToast();

  const uploadPhotos = async (
    parentType: PhotoParentType,
    parentId: string,
    files: File[],
    onProgress?: (photoFiles: PhotoFile[]) => void
  ): Promise<PhotoResponse[]> => {
    try {
      // Step 1: Request upload URLs
      const urlsResponse = await requestUrls.mutateAsync({
        parentType,
        parentId,
        photoCount: files.length,
        markFirstAsPrimary: true
      });

      if (!urlsResponse.uploadInfos || urlsResponse.uploadInfos.length === 0) {
        throw new Error("No upload URLs received");
      }

      // Step 2: Create PhotoFile objects
      const photoFiles: PhotoFile[] = files.map((file, index) => ({
        file,
        uploadInfo: urlsResponse.uploadInfos![index],
        progress: 0,
        status: 'pending'
      }));

      // Step 3: Upload files to Azure
      const uploadPromises = photoFiles.map(async (photoFile, index) => {
        try {
          photoFiles[index].status = 'uploading';
          onProgress?.(photoFiles);

          if (!photoFile.uploadInfo.uploadUrl) {
            throw new Error("Upload URL is missing");
          }

          await uploadFileToAzure(photoFile.file, photoFile.uploadInfo.uploadUrl);

          photoFiles[index].status = 'uploaded';
          photoFiles[index].progress = 100;
          onProgress?.(photoFiles);
        } catch (error) {
          photoFiles[index].status = 'error';
          photoFiles[index].error = error instanceof Error ? error.message : 'Upload failed';
          onProgress?.(photoFiles);
          throw error;
        }
      });

      await Promise.all(uploadPromises);

      // Step 4: Confirm uploads
      const confirmedPhotos: PhotoResponse[] = [];
      for (const photoFile of photoFiles) {
        if (photoFile.status === 'uploaded') {
          try {
            const confirmed = await confirmUpload.mutateAsync(photoFile.uploadInfo.photoId);
            confirmedPhotos.push(confirmed);
            photoFile.status = 'confirmed';
            onProgress?.(photoFiles);
          } catch (error) {
            photoFile.status = 'error';
            photoFile.error = 'Failed to confirm upload';
            onProgress?.(photoFiles);
          }
        }
      }

      toast({
        title: "Photos uploaded",
        description: `${confirmedPhotos.length} photo(s) uploaded successfully.`
      });

      return confirmedPhotos;
    } catch (error) {
      toast({
        title: "Upload failed",
        description: error instanceof Error ? error.message : "Failed to upload photos",
        variant: "destructive"
      });
      throw error;
    }
  };

  return {
    uploadPhotos,
    isUploading: requestUrls.isPending || confirmUpload.isPending
  };
}
